import { createSelector, createSlice } from "@reduxjs/toolkit";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { dateRangeOverlap } from "../../common/utils/dateUtils";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = meeting => meeting.id;

export const meetingMgrSlice = createSlice({
    name: 'mgrMeetings',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.ids.forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(meeting => state.data[meeting.id] = meeting);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(meeting => state.data[meeting.id] = meeting);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(meeting => delete state.data[meeting.id]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => meetingMgrSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'meeting_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'meeting_one');

const fetchMeeting = aggregatingFetch(
    id => network => network.post('/api/v2/meetings', [id],  data => meetingMgrSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/meetings', ids, data => meetingMgrSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
)
const fetchMeetingsInRange = (start, end) => network => network.get(`/api/v2/meetings/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, meetingMgrSlice.actions.addOrUpdate, visualizeFailure('get'));

export const createMeeting = (network, meeting) => network.put('/api/v1/meeting', meeting, meetingMgrSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateMeeting = (network, meeting) => network.post('/api/v1/meeting', meeting, meetingMgrSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeMeeting = (network, meeting) => network.delete('/api/v1/meeting/' + encodeURIComponent(meeting.id), meetingMgrSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectMeetingData = state => state.mgrMeetings.data;
const selectMeeting = id => createSelector([selectMeetingData], meetings => meetings[id]);
const selectMeetings = ids => createSelector([selectMeetingData], meetings => ids.reduce((result, id) => { if (meetings[id]) { result.push(meetings[id]); } return result; }, []));

const selectMeetingsInRange = (start, end) => createSelector([selectMeetingData], meetings => {
    return Object.values(meetings).filter(m => dateRangeOverlap(m, { start, end }));
})
// #endregion

const listen = {
    meetingCreated: (meeting, { dispatch, websocket }) => {
        markFetched(websocket, 'mgrMeeting', key(meeting))
        dispatch(meetingMgrSlice.actions.addOrUpdate(meeting))
    },
    meetingUpdated: (meeting, { dispatch }) => dispatch(meetingMgrSlice.actions.addOrUpdate(meeting)),
    meetingRemoved: (meeting, { dispatch }) => dispatch(meetingMgrSlice.actions.remove(meeting)),
}

const meetingInitByID = initMapFactory(fetchMeeting, listen);
registerEntityBasedFetch('mgrMeeting', meetingInitByID);

const cleanup = { root: meetingMgrSlice.name, all: state => Object.values(selectMeetingData(state)), removeAll: meetingMgrSlice.actions.remove, key };
export const [useMgrMeeting] = dataHookFactory(selectMeeting, meetingInitByID, { cleanup });
export const [useMgrMeetings] = dataHookFactory(selectMeetings, initMultiMapFactory(meetingInitByID, listen), { cleanup });
export const [useMgrMeetingsInRange] = dataHookFactory(selectMeetingsInRange, queryMapFactory(fetchMeetingsInRange, 'id', meetingInitByID, listen), { cleanup });

export const canRouteMeetings = ({credentials, clientConfig}) => credentials.hasRole(['administrator', 'event manager', 'training manager', 'meeting manager', 'template manager', 'auditor', 'event auditor', 'training auditor', 'meeting auditor', 'template auditor']) && clientConfig.enableMeetings

const reducer = { name: meetingMgrSlice.name, reducer: meetingMgrSlice.reducer }
export default reducer;